import {
	FollowUpBossAgentSelection,
	FollowUpBossAgentSelectionIhfAgent,
	FollowUpBossAgentSelectionRequest,
} from "model/follow-up-boss-agent-selection";
import { followUpBossCopy } from "../../follow-up-boss-copy";

// MAX-4236: every decision the agent selection table makes, as pure functions so they can be
// tested without rendering (the repo has no component-rendering tests). The .tsx beside this
// file only draws the state these functions produce.
//
// MAX-4579 (product decisions from acceptance testing): the unassigned/pond import toggle is
// gone (those contacts are never imported), and a saved selection is locked. The state is the
// rows alone.

// Placeholder copy, reviewed by product in acceptance testing (P7).
// MAX-4580: the two strings that named iHomefinder (the intro and the no-agent validation)
// moved to follow-up-boss-copy.ts, which carries both the direct and the reseller wording.
export const SYNC_NOW_HINT = "Run Sync Now on the Status tab to apply this to contacts already in Follow Up Boss.";
// Wizard only: shown under the load error so a FUB outage does not strand the customer on Step 2.
export const SELECTION_UNAVAILABLE_NOTE = "You can continue and choose which Follow Up Boss users to import later on the Settings tab.";
// Wizard only: an empty selection is valid but means nothing imports, which is easy to skim past.
export const NO_USERS_SELECTED_WARNING = "No users selected: no Follow Up Boss contacts will be imported.";
// MAX-4579: shown above Continue (wizard) and Save Changes (an unconfirmed Settings tab) before the
// selection locks; and the read-only note once it has.
export const SELECTION_LOCK_WARNING = "You can't change this selection later without contacting support. Check it before you continue.";
// MAX-4580: same sentence as the Status tab note, so the modal does not carry two wordings
// for the same fact. "Contact support" stays plain text, never a link (product decision 7).
export const SELECTION_LOCKED_MESSAGE = "Agents can't be changed after setup. To update, contact support.";
export const NO_SELECTION_LOCKED_MESSAGE = "No Follow Up Boss users are selected, so no contacts are imported. Contact support to change this.";

// Same wording as the server's cap rejection, singular for the common one-seat plan.
export function capMessage(seatCap: number): string {
	return `You can select up to ${seatCap} ${seatCap === 1 ? "agent" : "agents"} on your plan.`;
}

// A checked row whose agent has not been picked yet has agentId null; it cannot be saved.
export interface SelectionRow {
	fubUserId: number;
	agentId: number | null;
}

export interface AgentSelectionState {
	rows: SelectionRow[];
}

// Pre-fill from the server's pairs, dropping anything whose user or agent is not in the
// candidate lists (a deleted FUB user or a demoted agent can still be referenced by a row).
export function prefillRows(selection: FollowUpBossAgentSelection): SelectionRow[] {
	const userIds = new Set(selection.fubUsers.map((u) => u.id));
	const agentIds = new Set(selection.ihfAgents.map((a) => a.id));
	return selection.pairs
		.filter((p) => userIds.has(p.fubUserId) && agentIds.has(p.agentId))
		.map((p) => ({ fubUserId: p.fubUserId, agentId: p.agentId }));
}

export function initialSelectionState(selection: FollowUpBossAgentSelection): AgentSelectionState {
	return { rows: prefillRows(selection) };
}

export function isSelected(state: AgentSelectionState, fubUserId: number): boolean {
	return state.rows.some((r) => r.fubUserId === fubUserId);
}

export function agentFor(state: AgentSelectionState, fubUserId: number): number | null {
	const row = state.rows.find((r) => r.fubUserId === fubUserId);
	return row ? row.agentId : null;
}

export interface CapStatus {
	count: number;
	atCap: boolean;
	label: string;
}

export function capStatus(state: AgentSelectionState, seatCap: number | null | undefined): CapStatus {
	const count = state.rows.length;
	if (seatCap === null || seatCap === undefined) {
		return { count, atCap: false, label: `${count} selected` };
	}
	return { count, atCap: count >= seatCap, label: `${count} of ${seatCap} selected` };
}

// An already-selected row can always be toggled; a new one only below the cap.
export function canSelect(state: AgentSelectionState, seatCap: number | null | undefined, fubUserId: number): boolean {
	return isSelected(state, fubUserId) || !capStatus(state, seatCap).atCap;
}

export function selectUser(state: AgentSelectionState, seatCap: number | null | undefined, fubUserId: number): AgentSelectionState {
	if (isSelected(state, fubUserId) || !canSelect(state, seatCap, fubUserId)) {
		return state;
	}
	return { rows: [...state.rows, { fubUserId, agentId: null }] };
}

export function deselectUser(state: AgentSelectionState, fubUserId: number): AgentSelectionState {
	if (!isSelected(state, fubUserId)) {
		return state;
	}
	return { rows: state.rows.filter((r) => r.fubUserId !== fubUserId) };
}

export function setAgent(state: AgentSelectionState, fubUserId: number, agentId: number | null): AgentSelectionState {
	if (!isSelected(state, fubUserId)) {
		return state;
	}
	return { rows: state.rows.map((r) => (r.fubUserId === fubUserId ? { ...r, agentId } : r)) };
}

// Agents offered for one row: every eligible agent not already chosen in another row.
export function availableAgents(
	selection: FollowUpBossAgentSelection,
	state: AgentSelectionState,
	fubUserId: number,
): FollowUpBossAgentSelectionIhfAgent[] {
	const taken = new Set(state.rows.filter((r) => r.fubUserId !== fubUserId && r.agentId !== null).map((r) => r.agentId));
	return selection.ihfAgents.filter((a) => !taken.has(a.id));
}

// Client-side pass over the same rules the server enforces, so the common mistakes show
// without a round trip. The server message wins when it disagrees.
// MAX-4580: whiteLabel selects the reseller wording of the no-agent message. It cannot change
// whether the selection is valid, only how the failure reads.
export function validateSelection(state: AgentSelectionState, seatCap: number | null | undefined, whiteLabel: boolean): string | null {
	if (seatCap !== null && seatCap !== undefined && state.rows.length > seatCap) {
		return capMessage(seatCap);
	}
	if (state.rows.some((r) => r.agentId === null)) {
		return followUpBossCopy(whiteLabel).noAgentError;
	}
	return null;
}

// MAX-4579: the wizard cannot continue until every selected user has an agent. An empty
// selection is still allowed through (with the warning), as is a failed load (see connection.tsx).
export function canContinue(state: AgentSelectionState | null, seatCap: number | null | undefined): boolean {
	// the wording is irrelevant here, only whether a message was produced
	return state === null || validateSelection(state, seatCap, false) === null;
}

// MAX-4579: a confirmed selection is locked and is never PUT again; the Settings tab renders it
// read-only. An unconfirmed account (connected before its package gained agent selection) is
// always dirty: what the table shows is only a suggestion until the first save, and saving the
// tab saves what the customer sees, which then locks it.
export function isSelectionDirty(state: AgentSelectionState, selection: FollowUpBossAgentSelection): boolean {
	return !selection.confirmed;
}

export function isSelectionLocked(selection: FollowUpBossAgentSelection | null): boolean {
	return selection !== null && selection.confirmed;
}

// Read-only rendering once locked: the selected users with the agent each one maps to, in the
// server's order. A pair whose agent is no longer eligible is still shown by id so the customer
// sees what support will see.
export interface LockedPair {
	fubUserId: number;
	userName: string;
	userEmail: string | null;
	isOwner: boolean;
	agentId: number;
	agentName: string;
	// MAX-4580: the Status tab list shows the agent's email alongside the name
	agentEmail: string | null;
}

export function lockedPairs(selection: FollowUpBossAgentSelection): LockedPair[] {
	const agentsById = new Map(selection.ihfAgents.map((a) => [a.id, a] as [number, FollowUpBossAgentSelectionIhfAgent]));
	const result: LockedPair[] = [];
	for (const user of selection.fubUsers) {
		const pair = selection.pairs.find((p) => p.fubUserId === user.id);
		if (!pair) {
			continue;
		}
		const agent = agentsById.get(pair.agentId);
		result.push({
			fubUserId: user.id,
			userName: user.name,
			userEmail: user.email,
			isOwner: user.isOwner,
			agentId: pair.agentId,
			agentName: agent ? agent.name || agent.email || `Agent ${agent.id}` : `Agent ${pair.agentId}`,
			agentEmail: agent ? agent.email : null,
		});
	}
	return result;
}

export function buildSaveRequest(state: AgentSelectionState): FollowUpBossAgentSelectionRequest {
	const pairs = state.rows
		.filter((r): r is { fubUserId: number; agentId: number } => r.agentId !== null)
		.map((r) => ({ fubUserId: r.fubUserId, agentId: r.agentId }));
	return { pairs };
}
